import { memo, useEffect, useMemo, useRef } from "react";
import { Platform, View, type ViewStyle } from "react-native";

import useSessionStore from "@features/session/hooks/useSessionStore";
import { useAppTheme } from "@hooks/useAppTheme";
import usePlayer from "@memorang/features/src/audio/usePlayer";
import { TooltipWrapper } from "@memorang/ui";
import { IconButton } from "react-native-paper";

import { AIFeedbackMenu } from "../../session/components/AIFeedbackMenu";
import { base64ToMp3DataUri } from "../helpers/audio";
import { useProethicsFlowStore } from "../hooks/useProethicsFlowStore";
import type { TranscriptMessage } from "../types";

// RN's `ViewStyle` only models "auto" | "pointer", hence the cast; react-native-web renders any CSS cursor.
const helpCursorStyle =
	Platform.OS === "web" ? ({ cursor: "help" } as unknown as ViewStyle) : null;

// Latches synchronously during render, not in an effect: the bar mounts before audio exists, so an effect-time read would miss the first render.
const useAutoplayLatch = (messageId: string) => {
	const isTarget = useProethicsFlowStore(
		(s) => s.autoplayMessageId === messageId,
	);
	const latched = useRef(false);
	if (isTarget) {
		latched.current = true;
	}
	useEffect(() => {
		if (isTarget) {
			useProethicsFlowStore.setState({ autoplayMessageId: null });
		}
	}, [isTarget]);

	return latched.current;
};

const usePreemptedByOther = (
	player: ReturnType<typeof usePlayer>,
	messageId: string,
) => {
	const preempted = useProethicsFlowStore(
		(s) => s.playingMessageId !== null && s.playingMessageId !== messageId,
	);
	const { isPlaying, pauseSound, onSeek } = player;

	useEffect(() => {
		if (preempted && isPlaying) {
			pauseSound();
			onSeek(0);
		}
	}, [preempted, isPlaying, pauseSound, onSeek]);
};

// Ownership-guarded clear: only clears if still the owner, so a newly-started clip's claim isn't stomped by the previous clip's cleanup.
const usePlayingMessageReport = (isPlaying: boolean, messageId: string) => {
	useEffect(() => {
		if (isPlaying) {
			useProethicsFlowStore.setState({ playingMessageId: messageId });
			return;
		}
		useProethicsFlowStore.setState((s) =>
			s.playingMessageId === messageId ? { playingMessageId: null } : {},
		);
	}, [isPlaying, messageId]);

	useEffect(
		() => () => {
			useProethicsFlowStore.setState((s) =>
				s.playingMessageId === messageId ? { playingMessageId: null } : {},
			);
		},
		[messageId],
	);
};

const ActionIcon = ({
	icon,
	label,
	onPress,
	disabled = false,
	loading = false,
	cursorHelp = false,
}: {
	icon: string;
	label: string;
	onPress?: () => void;
	disabled?: boolean;
	loading?: boolean;
	cursorHelp?: boolean;
}) => {
	const theme = useAppTheme();
	return (
		<TooltipWrapper title={label}>
			<IconButton
				icon={icon}
				size={16}
				loading={loading}
				disabled={disabled}
				iconColor={theme.colors.onSurfaceVariant}
				style={[
					{ margin: 0, borderRadius: theme.roundness },
					cursorHelp ? helpCursorStyle : null,
				]}
				accessibilityLabel={label}
				onPress={onPress}
			/>
		</TooltipWrapper>
	);
};

const SpeakerButton = ({ message }: { message: TranscriptMessage }) => {
	// The error bubble appended in sendText's catch has no audioStatus; it reads as unavailable.
	const status = message.audioStatus ?? "unavailable";
	const uri = message.audioBase64
		? base64ToMp3DataUri(message.audioBase64)
		: (message.audioUrl ?? null);

	const latched = useAutoplayLatch(message.id);
	const player = usePlayer(uri, undefined, latched);
	usePlayingMessageReport(player.isPlaying, message.id);
	usePreemptedByOther(player, message.id);

	if (status === "pending") {
		return (
			<ActionIcon
				icon="volume-high"
				label="Generating audio"
				disabled
				loading
			/>
		);
	}
	if (status === "unavailable") {
		return (
			<ActionIcon
				icon="volume-high"
				label="No audio for this response"
				disabled
				cursorHelp
			/>
		);
	}

	const playing = player.isPlaying;
	return (
		<ActionIcon
			icon={playing ? "stop-circle" : "volume-high"}
			label={playing ? "Stop" : "Read aloud"}
			loading={!player.isLoaded}
			disabled={!player.isLoaded}
			onPress={() => {
				if (playing) {
					player.pauseSound();
					player.onSeek(0);
					return;
				}
				if (player.isEnded) {
					player.onSeek(0);
				}
				player.playSound();
			}}
		/>
	);
};

// memo: transcript patches during a turn re-render the whole thread, but
// untouched messages keep their object reference, so finished bars skip.
export const MessageActionBar = memo(function MessageActionBar({
	message,
}: {
	message: TranscriptMessage;
}) {
	const readOnly = useSessionStore((s) => s.inReviewMode);
	const threadId = useProethicsFlowStore((s) => s.threadId);
	const scenarioId = useProethicsFlowStore((s) => s.scenarioId);
	const theme = useAppTheme();

	const feedbackContext = useMemo(
		() =>
			JSON.stringify({
				scenarioId,
				threadId,
				messageId: message.id,
				message: message.text.slice(0, 300),
			}),
		[scenarioId, threadId, message.id, message.text],
	);

	return (
		<View style={{ flexDirection: "row", alignItems: "center", marginTop: 2 }}>
			<SpeakerButton message={message} />
			<AIFeedbackMenu
				itemId={scenarioId}
				featureName="AI Avatar"
				anchorIcon="dots-horizontal"
				anchorStyle={{ borderRadius: theme.roundness }}
				copyText={message.text}
				threadId={threadId ?? undefined}
				feedbackContext={feedbackContext}
				responseId={message.id}
				feedbackDisabled={readOnly}
			/>
		</View>
	);
});
